// Per-quote LOCAL draft of the customer's in-progress selections, mirrored to
// localStorage so a reload / navigate-away / accidental tab close never loses
// what they picked or typed. The configurator otherwise persists to the backend
// only on explicit Save / See my price / Add project; everything entered between
// those actions lived in volatile React state and vanished on reload. This is
// the edit-in-flight buffer that closes that gap.
//
// SCOPE — selections ONLY, never prices. We store the customer's own input
// (per-line config, project title, need-by date, the ship-to addresses, order
// services). We do NOT store unit_price / line_price / total_sale_amount /
// tier — caching any
// pricing data in the SPA is prohibited (CLAUDE.md §Caching). The saved-price
// snapshot keeps coming from the server quote, untouched, and a fresh "See my
// price" stays the only authoritative price.
//
// STALENESS — each draft records the server version it was edited on top of:
// its quote.updated_at (`base_updated_at`) and the server's selections and line
// ids at that version (`base_snapshot`, QuoteEdit's draftBaseSnapshot). On load the draft
// is applied only while the server's selections are still those (draftIsCurrent),
// so it holds genuine unsaved edits. A save that moved them — another tab,
// another device — makes it stale, so it is discarded and the server wins. Our
// own saves re-base the draft on what they wrote, so an edit made while one is in
// flight survives its refetch. updated_at alone is not the test: the price
// snapshot, a status sync and the Nexus link stamp all advance it without
// touching a selection, and treating those as stale reverted the customer's
// picks to the last saved copy. That keeps the backend the source of
// truth and the local draft a pure buffer for edits that haven't been saved yet.

import { normalizeOrderServices, normalizeShipTos } from 'shared';
import type { OrderServicesSnapshot, ShipTo } from 'shared';

export type QuoteDraftLine = {
  // Mirrors LineDraft.local_id — the persisted QuoteLine id, or a synthetic
  // "new-…" id for a line added but not yet saved.
  local_id: string;
  title: string;
  quantity: number;
  // Form-schema-shaped config (FieldValues). No price fields ever live here.
  config: Record<string, unknown>;
};

export type QuoteDraft = {
  // Shape version, so a future change can't mis-read an older blob.
  v: 1;
  // The server quote.updated_at this draft was built on. See "STALENESS" above.
  base_updated_at: string;
  // The server's selections and line ids at that version, as one string. Optional
  // so a draft written before it existed still validates; such a draft falls
  // back to the updated_at match alone.
  base_snapshot?: string;
  // Client clock at write time — used only for the TTL sweep below, never for
  // correctness (base_updated_at governs whether a draft is applied).
  saved_at: number;
  project_title: string;
  requested_finish_date: string;
  // The explicit rush choice. Optional so a draft written before this field
  // existed still validates (the load path falls back to the server flag).
  expedite_requested?: boolean;
  // An older draft's quote-level "art to follow" boolean. Read only, to seed
  // each line's per-slot marks (config.artwork_pending_roles) on load; never
  // written.
  artwork_pending?: boolean;
  // Every address the order ships to, in order. OPTIONAL so a draft written
  // before split shipping existed still validates — that one carries the older
  // single `ship_to` below instead, and the load path falls back to the server
  // list, which is what stops an older draft from collapsing a saved split back
  // to one address.
  ship_tos?: ShipTo[];
  // The single address a pre-split draft carried. Kept readable so an in-flight
  // draft written by the previous shape is not thrown away on the next load.
  ship_to?: ShipTo;
  // The printed proof sample (FB-0036): how many, and where each is mailed.
  // Both OPTIONAL for the same back-compat reason as the fields above — a draft
  // written before the capture existed carries neither, and the load path falls
  // back to the server's answer rather than clearing one the customer saved.
  proof_quantity?: number | null;
  proof_ship_tos?: ShipTo[];
  order_services: OrderServicesSnapshot;
  lines: QuoteDraftLine[];
};

const KEY_PREFIX = 'pp.quote-draft.';
// Abandoned drafts older than this are swept on read — a safety net so a draft
// for a quote that's never reopened can't linger forever. The base_updated_at
// match is what actually decides whether a draft is applied.
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

function key(quoteId: string): string {
  return `${KEY_PREFIX}${quoteId}`;
}

// localStorage can throw (private mode, storage disabled, quota). Treat any
// failure as "no persistence" rather than letting it break the editor — the
// backend save path remains the durable store.
function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// The last draft this tab wrote, per quote, as the JSON round trip would read it
// back. A localStorage write can fail — the quota is per origin and shared by
// every quote a rep has opened in it — and the save that follows re-stamps
// whatever storage still holds, so the refetch after it would hydrate from the
// server copy that predates the edit. Reads in this tab take this copy first, so
// the editor's hand-off between a save and its refetch never depends on storage.
// localStorage is still what carries a draft across a reload.
const writtenThisTab = new Map<string, QuoteDraft>();

export function readQuoteDraft(quoteId: string): QuoteDraft | null {
  const own = writtenThisTab.get(quoteId);
  if (own) return own;
  const s = storage();
  if (!s) return null;
  const raw = s.getItem(key(quoteId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<QuoteDraft>;
    // Validate the full shape — including every line element — before trusting
    // it. A corrupt or older-shape blob otherwise spreads `undefined` into
    // local_id / quantity / config, and config flows straight into the next
    // PATCH body. This is the `unknown` + validate-at-boundary convention. Any
    // failure discards the entry so it can't be re-read.
    const linesOk =
      Array.isArray(parsed.lines) &&
      parsed.lines.every((l) => {
        const dl = l as Partial<QuoteDraftLine> | null;
        return (
          dl != null &&
          typeof dl.local_id === 'string' &&
          typeof dl.title === 'string' &&
          typeof dl.quantity === 'number' &&
          typeof dl.config === 'object' &&
          dl.config !== null
        );
      });
    const valid =
      parsed?.v === 1 &&
      typeof parsed.base_updated_at === 'string' &&
      (parsed.base_snapshot === undefined || typeof parsed.base_snapshot === 'string') &&
      typeof parsed.saved_at === 'number' &&
      typeof parsed.project_title === 'string' &&
      typeof parsed.requested_finish_date === 'string' &&
      parsed.order_services != null &&
      // One of the two shapes has to be there. Absent `ship_tos` is fine on a
      // draft older than split shipping — it carries `ship_to` instead — but
      // present-and-not-an-array is corruption, and this blob flows into React
      // state and the next PATCH body, so it is discarded rather than trusted.
      (parsed.ship_tos === undefined || Array.isArray(parsed.ship_tos)) &&
      (parsed.ship_tos != null || parsed.ship_to != null) &&
      // Same rule for the printed samples' addresses: absent is fine (an older
      // draft, or a quote with no physical proof), present-and-not-an-array is
      // corruption in a blob that flows into React state and the next PATCH.
      (parsed.proof_ship_tos === undefined || Array.isArray(parsed.proof_ship_tos)) &&
      linesOk;
    if (!valid) {
      s.removeItem(key(quoteId));
      return null;
    }
    // TTL sweep — an abandoned draft older than MAX_AGE_MS is discarded (a
    // missing/non-number saved_at was already rejected above, so this can't be
    // bypassed by an entry that simply omits the field).
    if (Date.now() - (parsed.saved_at as number) > MAX_AGE_MS) {
      s.removeItem(key(quoteId));
      return null;
    }
    // Narrow the address list through the SHARED normalizer rather than a
    // hand-rolled shape check, so the editor can never be handed a list the
    // backend would have dropped keys from. A pre-split draft's single `ship_to`
    // is promoted into the list here, so the load path has one shape to read.
    const ship_tos =
      normalizeShipTos(parsed.ship_tos) ?? normalizeShipTos(parsed.ship_to ? [parsed.ship_to] : []);
    // The samples' addresses go through the same shared normalizer, for the same
    // reason. They have no pre-split single-value shape to promote.
    const proof_ship_tos = normalizeShipTos(parsed.proof_ship_tos);
    // The order services go through the shared normalizer too, so the editor is
    // never handed a key the backend would drop. That includes an
    // `order_services.assembly` kit, which the normalizer does not admit: a
    // buffer cannot put one back into the next PATCH, because the one assembly
    // flow is the item (ADR-020). A value it cannot read at all is corruption,
    // discarded like the rest.
    const order_services = normalizeOrderServices(parsed.order_services);
    if (!order_services) {
      s.removeItem(key(quoteId));
      return null;
    }
    return {
      ...(parsed as QuoteDraft),
      ship_tos: ship_tos ?? undefined,
      proof_ship_tos: proof_ship_tos ?? undefined,
      order_services,
    };
  } catch {
    s.removeItem(key(quoteId));
    return null;
  }
}

// Whether a draft still sits on the server's current selections. See
// "STALENESS" above.
export function draftIsCurrent(
  draft: QuoteDraft,
  serverUpdatedAt: string,
  serverSnapshot: string,
): boolean {
  return draft.base_updated_at === serverUpdatedAt || draft.base_snapshot === serverSnapshot;
}

function trySetItem(s: Storage, k: string, value: string): boolean {
  try {
    s.setItem(k, value);
    return true;
  } catch {
    return false;
  }
}

// Make room for `keepKey` by discarding OTHER quotes' drafts: every expired or
// unreadable one first, then the oldest by saved_at, one at a time until the
// write fits. Only drafts ever go — a draft is a buffer for unsaved edits, and
// the TTL sweep in readQuoteDraft only ever reaches the quote being opened, so
// abandoned ones otherwise pile up until the quota refuses every write.
function writeEvictingOldDrafts(s: Storage, keepKey: string, value: string): boolean {
  const others: { k: string; savedAt: number }[] = [];
  for (let i = 0; i < s.length; i += 1) {
    const k = s.key(i);
    if (!k || !k.startsWith(KEY_PREFIX) || k === keepKey) continue;
    let savedAt = Number.NEGATIVE_INFINITY;
    try {
      const parsed = JSON.parse(s.getItem(k) ?? '') as Partial<QuoteDraft> | null;
      if (typeof parsed?.saved_at === 'number') savedAt = parsed.saved_at;
    } catch {
      // Unreadable — first to go.
    }
    others.push({ k, savedAt });
  }
  const expiredBefore = Date.now() - MAX_AGE_MS;
  for (const o of others) {
    if (o.savedAt < expiredBefore) s.removeItem(o.k);
  }
  if (trySetItem(s, keepKey, value)) return true;
  const live = others.filter((o) => o.savedAt >= expiredBefore).sort((a, b) => a.savedAt - b.savedAt);
  for (const o of live) {
    s.removeItem(o.k);
    if (trySetItem(s, keepKey, value)) return true;
  }
  return false;
}

export function writeQuoteDraft(quoteId: string, draft: QuoteDraft): void {
  let value: string;
  try {
    value = JSON.stringify(draft);
  } catch {
    return;
  }
  writtenThisTab.set(quoteId, JSON.parse(value) as QuoteDraft);
  const s = storage();
  if (!s) return;
  try {
    if (trySetItem(s, key(quoteId), value) || writeEvictingOldDrafts(s, key(quoteId), value)) return;
    // Even an empty store refused it. What storage still holds for this quote is an
    // OLDER draft, and the next tab to open the quote would overlay it whenever its
    // base still matches the server, reverting every pick made since — a paper
    // stock snapping back on every line. No draft is better than that one.
    s.removeItem(key(quoteId));
  } catch {
    // Storage failure — this tab's copy above still holds the draft.
  }
}

export function clearQuoteDraft(quoteId: string): void {
  writtenThisTab.delete(quoteId);
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(key(quoteId));
  } catch {
    /* ignore */
  }
}
